/**
 * ⚠️ APEXCHARTS VERSION WARNING ⚠️
 *
 * DO NOT UPGRADE apexcharts beyond v5.10.6
 *
 * Issue: Apex Charts v5+ has a known tooltip bug that breaks tooltip display
 * in mixed chart types (bar + line series). The tooltip shows incorrect values
 * or fails to render properly when using multiple y-axis with different scales.
 *
 * Current workaround: Using tooltip.shared=false and tooltip.intersect=true
 * in the chart options (line 193-197), but this is not a complete fix.
 *
 * See: https://github.com/apexcharts/apexcharts.js/issues/XXXX
 *
 * Before upgrading: Test the dividends graph thoroughly, especially tooltip behavior
 * on bar hover with multiple series.
 */
import { useMemo } from "react";
import Chart from "react-apexcharts";

import Dropdown from "@/components/Dropdown";
import Typography from "@/components/Typography";

const barAxis: any = {
  seriesName: "Dividendos",
  labels: {
    showDuplicates: false,
    style: {
      colors: "#818A8F",
    },
    formatter: function (value: number): string {
      return value.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
  title: {
    text: "Dividendos (R$)",
    style: {
      fontWeight: 700,
    },
  },
};

const barAxisWithoutShow: any = {
  seriesName: "Dividendos",
  show: false,
  labels: {
    showDuplicates: false,
    style: {
      colors: "#818A8F",
    },
    formatter: function (value: number): string {
      return value.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
  title: {
    text: "Dividendos (R$)",
    style: {
      fontWeight: 700,
    },
  },
};

const lineAxis: any = {
  seriesName: "Porcentagem",
  opposite: true,
  show: true,
  title: {
    text: "10% / Yield Mensal (%)",
  },
  min: 0,
  max: 2,
  labels: {
    formatter: (value: number) => `${value.toFixed(2)}%`,
  },
};

const lineAxisWithoutShow: any = {
  seriesName: "Porcentagem",
  opposite: true,
  show: false,
  title: {
    text: "10% / Yield Mensal (%)",
  },
  min: 0,
  max: 2,
  labels: {
    formatter: (value: number) => `${value.toFixed(2)}%`,
  },
};

const baseOptions = {
  annotations: {},
  fontColor: "#818A8F",
  chart: {
    animations: {
      enabled: false,
    },
    background: "#fff",
    fontFamily: "Inter",
    height: 250,
    id: "uRSqT",
    stacked: false,
    toolbar: {
      show: false,
    },
    width: 400,
    zoom: {
      allowMouseWheelZoom: true,
    },
  },
  responsive: [
    {
      breakpoint: 768,
      options: {
        chart: {
          height: 350,
        },
        legend: {
          fontSize: 11,
          markers: {
            size: 5,
          },
        },
        xaxis: {
          labels: {
            style: {
              fontSize: "11px",
            },
          },
        },
      },
    },
  ],
  plotOptions: {
    bar: {
      borderRadius: 6,
      borderRadiusApplication: "end",
      borderRadiusWhenStacked: "last",
      hideZeroBarsWhenGrouped: false,
      isDumbbell: false,
      isFunnel: false,
      isFunnel3d: true,
      colors: {
        backgroundBarColors: ["#f1f2f3"],
        backgroundBarOpacity: 0,
      },
      dataLabels: {
        total: {
          enabled: false,
          offsetX: 0,
          offsetY: 0,
          style: {
            color: "#373d3f",
            fontSize: "12px",
            fontWeight: 600,
          },
        },
      },
    },
    line: {
      stroke: {
        curve: "straight",
      },
    },
  },
  colors: ["#5EC874", "#466560", "#748C88", "#7BB4AA", "#235950", "#DC2626"],
  dataLabels: {
    enabled: false,
    style: {
      fontWeight: 700,
    },
    background: {
      dropShadow: {},
    },
  },
  grid: {
    padding: {
      right: 25,
      left: 15,
    },
  },
  legend: {
    fontSize: 14,
    offsetY: 0,
    markers: {
      size: 7,
      strokeWidth: 4,
      shape: "line",
    },
    itemMargin: {
      vertical: 0,
      horizontal: 10,
    },
    labels: {
      colors: "#818A8F",
    },
    container: {
      maxColumns: 3,
    },
  },
  markers: {
    size: 4,
  },
  tooltip: {
    shared: false,
    hideEmptySeries: false,
    intersect: true,
  },
  xaxis: {
    labels: {
      trim: true,
      style: {
        colors: "#818A8F",
      },
    },
    group: {
      groups: [],
      style: {
        colors: [],
        fontSize: "12px",
        fontWeight: 400,
        cssClass: "",
      },
    },
    tickPlacement: "between",
    title: {
      style: {
        fontWeight: 700,
      },
    },
    tooltip: {
      enabled: false,
    },
    categories: [
      "Jan",
      "Fev",
      "Mar",
      "Abr",
      "Mai",
      "Jun",
      "Jul",
      "Ago",
      "Set",
      "Out",
      "Nov",
      "Dez",
    ],
  },
  theme: {
    palette: "palette2",
  },
};

const DividendsGraphSection = ({ data, months, filters, currentFilter, setCurrentFilter }) => {
  const options: any = useMemo(() => {
    const hasData = data && data.length > 0;
    const barSeriesCount = hasData ? data.length - 2 : 0; // -2 because the last 2 are line series

    // Find the index of the bar series with the maximum value
    let maxBarSeriesIndex = 0;
    let maxBarValue = -Infinity;
    let globalMax = 0;
    let globalMin = 0;

    // For line series scale
    let lineMax = -Infinity;
    let lineMin = Infinity;

    if (hasData && barSeriesCount > 0) {
      // Calculate bar series min/max
      for (let i = 0; i < barSeriesCount; i++) {
        const seriesData = data[i].data;
        const seriesMax = seriesData && seriesData.length > 0 ? Math.max(...seriesData) : 0;
        const seriesMin = seriesData && seriesData.length > 0 ? Math.min(...seriesData) : 0;
        if (seriesMax > maxBarValue) {
          maxBarValue = seriesMax;
          maxBarSeriesIndex = i;
        }
        if (seriesMax > globalMax) globalMax = seriesMax;
        if (seriesMin < globalMin) globalMin = seriesMin;
      }

      // Calculate line series min/max (last 2 series are lines)
      for (let i = barSeriesCount; i < data.length; i++) {
        const seriesData = data[i].data;
        const seriesMax = seriesData && seriesData.length > 0 ? Math.max(...seriesData) : 0;
        const seriesMin = seriesData && seriesData.length > 0 ? Math.min(...seriesData) : 0;
        if (seriesMax > lineMax) lineMax = seriesMax;
        if (seriesMin < lineMin) lineMin = seriesMin;
      }
    }

    // Create yaxis array with barAxis at the position of the series with max value
    // All bar axes share the same scale (min/max) so bars are proportional
    const yaxisArray: any[] = [];
    for (let i = 0; i < barSeriesCount; i++) {
      const axis = i === maxBarSeriesIndex ? { ...barAxis } : { ...barAxisWithoutShow };
      axis.min = globalMin;
      axis.max = globalMax;
      yaxisArray.push(axis);
    }
    yaxisArray.push(lineAxis, lineAxisWithoutShow);

    const options = {
      ...baseOptions,
      xaxis: {
        ...baseOptions.xaxis,
        categories: months || baseOptions.xaxis.categories,
      },
      // Configure axes: each bar series uses its corresponding yAxisIndex, line series use the last indices
      yaxis: yaxisArray,
      stroke: {
        // Only the line series (Dividend Yield and 10%) should have a border width
        width: hasData
          ? Array.from({ length: data.length }, (_, index) => (index >= data.length - 2 ? 4 : 0))
          : [0],
      },
    };

    return options;
  }, [data, months]);

  return (
    <section className="flex h-fit flex-col gap-5 pt-8">
      <div className="flex h-fit items-center justify-between">
        <Typography variant="h4" color="black">
          Dividendos
        </Typography>
        {filters && filters.length > 0 && (
          <Dropdown
            options={filters}
            onSelect={(e) => setCurrentFilter(e)}
            defaultValue={currentFilter}
          />
        )}
      </div>
      <div className="scrollbar-thin scrollbar-thumb-grey300 scrollbar-track-transparent w-full overflow-x-auto rounded-3xl">
        <div className="min-w-[700px] rounded-3xl bg-white p-4 md:min-w-[600px]">
          <Chart options={options} series={data} type="bar" width="100%" height="290px" />
        </div>
      </div>
    </section>
  );
};

export default DividendsGraphSection;
